import "./sidebar.css";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import AppApi from "../api/CognitoApi";
import React, { useState, useEffect, useCallback } from "react";
import { useMEConfig, useUserRole, useHistoricalForecast, useShortTermForecast, useRenewableForecast, useAilDemand, useAlarmData, useRevenue, useRunningRebootTargets, usePowerplantStatus, usePowerplantTotalOutput } from "../api/GetData";

const Api = new AppApi();

export const Sidebar = ({ visibleSections, plantName }) => {
  const { data: MEConfig } = useMEConfig(plantName);
  const {data:userProfile} = useUserRole()
  const { data: historicalData } = useHistoricalForecast();
  const { data: shortTermForecast } = useShortTermForecast();
  const { data: renewableData } = useRenewableForecast();
  const { data: ailDemandData } = useAilDemand();
  const { data: alarmData } = useAlarmData();
  const {
    data: runningTargets = [],
    refetch: refetchRunningTargets,
    isLoading: runningTargetsLoading,
    isFetching: runningTargetsFetching,
    error: runningTargetsError,
  } = useRunningRebootTargets();
  const { data: revenueRaw } = useRevenue(plantName);
  const role = (userProfile || "").toLowerCase();
  const isOperator = role === "operator";
  const [renewables, setRenewables] = useState([]);
  const [renewableTimes, setRenewableTimes] = useState([]);
  const [CheckbotAlarm, setCheckbotAlarm] = useState(null);
  const [ErrorDataIngestion, setErrorDataIngestion] = useState(null);
  const [RunWISDMFail, setRunWISDMFail] = useState(null);
  const [SchedulerBotFailureAlarm, setSchedulerBotFailureAlarm] =
    useState(null);
  const [currentMonthRevenue, setCurrentMonthRevenue] = useState(0.0);
  const [previousMonthRevenue, setPreviousMonthRevenue] = useState(0.0);
  const [Screenwidth, setWidth] = useState(window.innerWidth >= 721);
  const [sideButton, setSideButton] = useState(false);
  const [rebootConfirmOpen, setRebootConfirmOpen] = useState(false);
  const [selectedRebootTarget, setSelectedRebootTarget] = useState(null);
  const [rebootStatusById, setRebootStatusById] = useState({});

  // CONFIG VARIABLES
  const [minrunhours, setMinRunHours] = useState(0);
  const [maxunprofhours, setMaxUnprofHours] = useState(0);
  const [assetidentifier, setAssetIdentifier] = useState("");
  const [assetname, setAssetName] = useState("");
  const [assettype, setAssetType] = useState("");
  const [actualcapacity, setActualCapacity] = useState(0);
  const [minimumstablegeneration, setMinimumStableGeneration] = useState(0);
  const [marginalcost, setMarginalCost] = useState(0);
  const [offercontrolparty, setOfferControlParty] = useState("");
  const [poweravailability, setPowerAvailability] = useState(0);
  const [optimizedRunLogic, setOptimizedRunLogic] = useState(false);
  const [mcRunLogic, setMcRunLogic] = useState(false);
  const [defaultLogic, setDefaultLogic] = useState(false);
  const [minProfit, setMinProfit] = useState(0);
  const [unavailableHours, setUnavailableHours] = useState([]);
  const [currentPlant, setCurrentPlant] = useState(plantName || "");
  const { data: powerplantStatus } = usePowerplantStatus(assetidentifier);
  const { data: totalOutputData } = usePowerplantTotalOutput(plantName);
  const monthLabelFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  });
  const summaryCurrentMonthLabel = monthLabelFormatter.format(new Date());
  const summaryPreviousMonthLabel = monthLabelFormatter.format(
    new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)
  );
  const rebootTargetsErrorMessage =
    (typeof runningTargetsError?.response?.data?.body === "string" &&
    runningTargetsError.response.data.body.trim())
      ? runningTargetsError.response.data.body
      : runningTargetsError?.response?.data?.message ||
        runningTargetsError?.response?.data?.error ||
        runningTargetsError?.message ||
        "";

  // APPLY MEConfig
  useEffect(() => {
    if (plantName && plantName !== currentPlant) {
      setCurrentPlant(plantName);
      setMinRunHours(0);
      setMaxUnprofHours(0);
      setAssetIdentifier("");
      setAssetName("loading...");
      setAssetType("");
      setActualCapacity(0);
      setMinimumStableGeneration(0);
      setMarginalCost(0);
      setOfferControlParty("loading...");
      setPowerAvailability(0);
      setMinProfit(0);
      setUnavailableHours([]);
      setOptimizedRunLogic(false);
      setMcRunLogic(false);
      setDefaultLogic(false);
    }
    if (!MEConfig || typeof MEConfig !== "object") {
      return;
    }
    setMinRunHours(MEConfig?.min_run_hours ?? 0);
    setMaxUnprofHours(MEConfig?.max_unprof_hours ?? 0);
    setAssetIdentifier(MEConfig?.asset_identifier ?? "");
    setAssetName(MEConfig?.asset_name ?? "loading...");
    setAssetType(MEConfig?.asset_type ?? "loading...");
    setActualCapacity(MEConfig?.actual_capacity ?? 0);
    setMinimumStableGeneration(
      MEConfig?.minimum_stable_generation ?? 0
    );
    setMarginalCost(MEConfig?.marginal_cost ?? 0);
    setOfferControlParty(MEConfig?.offer_control_party ?? "loading...");
    setPowerAvailability(MEConfig?.power_availability ?? 0);
    setMinProfit(MEConfig?.min_profit ?? 0);
    setUnavailableHours(MEConfig?.unavailable_hours ?? []);
    setOptimizedRunLogic(MEConfig?.optimizedRunLogic ?? false);
    setMcRunLogic(MEConfig?.mcRunLogic ?? false);
    setDefaultLogic(MEConfig?.defaultLogic ?? false);
  }, [MEConfig, plantName, currentPlant]);

  // VISIBILITY
  const {
    ActualPrice = true,
    ForecastPrice = true,
    AilDemand = true,
    RenewablesBox = true,
    WisdmStatus = true,
    WilsonCreek = true,
    Summary = true,
    AssetName = true,
    AssetIdentifier = true,
    AssetType = true,
    ActualCapacity = true,
    MinimumStableGeneration = true,
    MarginalCost = true,
    OfferControlParty = true,
    PowerAvailability = true,
    MinProfit = true,
    UnavailableHours = true,
    OptimizedRunLogic = true,
    McRunLogic = true,
    DefaultLogic = true,
    MinRunHours = true,
    MaxUnprofHours = true,
  } = visibleSections || {};

  // WINDOW RESIZE LISTENER
  useEffect(() => {
    const handleResize = () =>
      setWidth(window.innerWidth >= 721);
    window.addEventListener("resize", handleResize);
    return () =>
      window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    console.log("[rebootTargets] role:", role);
    console.log("[rebootTargets] WisdmStatus visible:", WisdmStatus);
    console.log("[rebootTargets] loading:", runningTargetsLoading, "fetching:", runningTargetsFetching);
    console.log("[rebootTargets] error:", runningTargetsError);
    console.log("[rebootTargets] targets:", runningTargets);
  }, [
    role,
    WisdmStatus,
    runningTargets,
    runningTargetsLoading,
    runningTargetsFetching,
    runningTargetsError,
  ]);

  // SORT ALARMS
  const sortAlarms = useCallback(() => {
    if (Array.isArray(alarmData)) {
      alarmData.forEach((alarm) => {
        switch (alarm.AlarmName) {
          case "CheckbotAlarm":
            setCheckbotAlarm(alarm.AlarmState === "OK");
            break;
          case "Error In Data Ingestion pipeline":
            setErrorDataIngestion(alarm.AlarmState === "OK");
            break;
          case "RUNWISDM Failure to Execute":
            setRunWISDMFail(alarm.AlarmState === "OK");
            break;
          case "SchedulerBotFailureAlarm":
            setSchedulerBotFailureAlarm(alarm.AlarmState === "OK");
            break;
        }
      });
    }
  }, [alarmData]);

  // REVENUE + RENEWABLES
  useEffect(() => {
    const parseRevenueRows = (raw) => {
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }
      if (typeof raw === "object") {
        if (Array.isArray(raw.body)) return raw.body;
        if (typeof raw.body === "string") {
          try {
            const parsed = JSON.parse(raw.body);
            return Array.isArray(parsed) ? parsed : [];
          } catch (error) {
            return [];
          }
        }
        if (Array.isArray(raw.data)) return raw.data;
      }
      return [];
    };

    const toSortableTime = (value) => {
      if (!value) return 0;
      const parsed = new Date(String(value).replace(" ", "T"));
      return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    };
    const rows = parseRevenueRows(revenueRaw);
    const sortedRows = [...rows].sort(
      (a, b) => toSortableTime(a?.datetime) - toSortableTime(b?.datetime)
    );
    let currentTot = 0;
    let previousTot = 0;
    const now = new Date();
    sortedRows.forEach((row) => {
      const tng = parseFloat(row?.TNG ?? row?.tng ?? 0);
      const pool = parseFloat(row?.poolprice ?? row?.pool_price ?? row?.poolPrice ?? 0);
      const mc = parseFloat(marginalcost ?? 0);
      const lineTotal = tng * Math.max(pool, mc);
      const timestamp = new Date(String(row?.datetime ?? "").replace(" ", "T"));
      if (Number.isNaN(timestamp.getTime())) {
        return;
      }
      const monthOffset =
        (timestamp.getFullYear() - now.getFullYear()) * 12 +
        (timestamp.getMonth() - now.getMonth());
      if (monthOffset === 0) {
        currentTot += lineTotal;
      } else if (monthOffset === -1) {
        previousTot += lineTotal;
      }
    });
    setCurrentMonthRevenue(currentTot);
    setPreviousMonthRevenue(previousTot);
    sortAlarms();
  }, [revenueRaw, sortAlarms]);

  useEffect(() => {
    if (!renewableData) {
      setRenewables([]);
      setRenewableTimes([]);
      return;
    }
    const times = renewableData.time || [];
    const solar = renewableData.solar || [];
    const wind = renewableData.wind || [];
    if (times.length === 0 || solar.length === 0 || wind.length === 0) {
      setRenewables([]);
      setRenewableTimes([]);
      return;
    }
    const combined = times.map((t, idx) => ({
      time: t,
      solar: parseFloat(solar[idx] || 0),
      wind: parseFloat(wind[idx] || 0),
    })).sort((a, b) => new Date(a.time) - new Date(b.time));

    // Renewable datetimes are start-of-hour (e.g. "13:00:00" = HE 14)
    // Match T by current local hour, T+1 by next hour
    const currentHour = new Date().getHours();
    const tEntry = combined.find(item => new Date(item.time).getHours() === currentHour);
    const t1Entry = combined.find(item => new Date(item.time).getHours() === (currentHour + 1) % 24);

    setRenewables([
      { solar: tEntry?.solar ?? 0, wind: tEntry?.wind ?? 0 },
      { solar: t1Entry?.solar ?? 0, wind: t1Entry?.wind ?? 0 },
    ]);
    setRenewableTimes([tEntry?.time, t1Entry?.time]);
  }, [renewableData]);

  const openRebootConfirm = (target) => {
    setSelectedRebootTarget(target);
    setRebootConfirmOpen(true);
  };

  const handleReboot = async () => {
    if (!selectedRebootTarget?.InstanceId) {
      setRebootConfirmOpen(false);
      return;
    }
    const instanceId = selectedRebootTarget.InstanceId;
    const name = selectedRebootTarget.Name || "";
    try {
      await Api.rebootInstance(instanceId, name);
      setRebootStatusById((prev) => ({
        ...prev,
        [instanceId]: { ok: true, message: "Reboot sent successfully." },
      }));
      refetchRunningTargets();
    } catch (error) {
      const apiError = error?.response?.data;
      const message =
        (typeof apiError?.body === "string" ? apiError.body : "") ||
        apiError?.error ||
        apiError?.message ||
        "Failed to reboot instance.";
      setRebootStatusById((prev) => ({
        ...prev,
        [instanceId]: { ok: false, message: String(message) },
      }));
    } finally {
      setRebootConfirmOpen(false);
      setSelectedRebootTarget(null);
    }
  };

  // Utility card wrapper
  const Card = ({ title, children }) => (
    <div className="sidebar-card">
      <div className="sidebar-card-header" style={{ textAlign: "center" }}>
        {title}
      </div>
      <div className="sidebar-card-content">{children}</div>
    </div>
  );

  return (
    <>
    <div>
      {!Screenwidth ? (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setSideButton(!sideButton)}
          className="sideButton"
          aria-label="Side Bar"
        >
          Side Bar
        </Button>
      ) : (
        <div />
      )}

      {(Screenwidth || sideButton) && (
        <Box className="left-column">

          {/* ACTUAL PRICE */}
          {ActualPrice && (
            <Card title="Actual Power Price">
              {(() => {
                if (!Array.isArray(historicalData) || historicalData.length === 0) {
                  return <>HE ? (t-1): $Error</>;
                }
                const sorted = [...historicalData].sort(
                  (a, b) => new Date(a.datetime) - new Date(b.datetime)
                );
                const latest = sorted[sorted.length - 1] || {};
                const dataHE = latest.datetime ? new Date(latest.datetime).getHours() : null;
                const currentHE = new Date().getHours() + 1;
                const offset = dataHE !== null ? currentHE - dataHE : null;
                const tLabel = offset === null ? "?" : offset === 0 ? "t" : `t-${offset}`;
                const price = latest.pool_price ?? latest.poolprice;
                return <>HE {dataHE ?? "?"} ({tLabel}): ${Number(price ?? 0).toFixed(0)}</>;
              })()}
            </Card>
          )}

          {/* FORECAST PRICE */}
          {ForecastPrice && (
            <Card title="Forecast Power Price">
              {(() => {
                if (!Array.isArray(shortTermForecast) || shortTermForecast.length === 0) {
                  return <>HE ? (t): $Error</>;
                }
                const sorted = [...shortTermForecast].sort(
                  (a, b) => new Date(a.datetime) - new Date(b.datetime)
                );
                const now = new Date();
                now.setMinutes(0, 0, 0);
                const hourMillis = 60 * 60 * 1000;
                const targetTimes = [0, 1, 2].map((offset) => new Date(now.getTime() + offset * hourMillis));

                return targetTimes.map((target, idx) => {
                  const match = sorted.find((entry) => {
                    const dt = new Date(entry.datetime);
                    return !Number.isNaN(dt.getTime()) && dt.getTime() === target.getTime();
                  });
                  const he = target.getHours() + 1;
                  const price = match?.forecast_price ?? match?.forecastPrice ?? match?.pool_price;
                  const label = idx === 0 ? "t" : `t+${idx}`;
                  return (
                    <div key={`${he}-${label}`}>
                      HE {he} ({label}): ${Number(price ?? 0).toFixed(0)}
                    </div>
                  );
                });
              })()}
            </Card>
          )}

          {/* AIL DEMAND */}
          {AilDemand && !isOperator && (
            <Card title="AIL Demand">
              {ailDemandData && ailDemandData.datetime ? (
                (() => {
                  const dt = new Date(ailDemandData.datetime);
                  const he = Number.isNaN(dt.getTime()) ? "?" : dt.getHours() + 1;
                  const demand = ailDemandData.aildemand ?? "Error";
                  return <>HE {he}: {demand} MW</>;
                })()
              ) : (
                <>HE ? : Error</>
              )}
            </Card>
          )}

          {/* RENEWABLES */}
          {RenewablesBox && !isOperator && (
            <Card title="Renewables">
              {renewables.length > 0 ? (
                <>
                  HE {renewableTimes[0] ? new Date(renewableTimes[0]).getHours() + 1 : "?"} (t):
                  {" "}Solar {Number(renewables[0].solar ?? 0).toFixed(0)} MW, Wind {Number(renewables[0].wind ?? 0).toFixed(0)} MW
                  <br />
                  HE {renewableTimes[1] ? new Date(renewableTimes[1]).getHours() + 1 : "?"} (t+1):
                  {" "}Solar {Number(renewables[1].solar ?? 0).toFixed(0)} MW, Wind {Number(renewables[1].wind ?? 0).toFixed(0)} MW
                </>
              ) : (
                <>HE ? : Error</>
              )}
            </Card>
          )}

          {/* WISDM STATUS */}
          {WisdmStatus && role === "dev" && (
            <Card title="WISDM Status">
              {Array.isArray(alarmData) && alarmData.length > 0 ? (
                alarmData.map((alarm) => (
                  <div className="status-row" key={alarm.AlarmArn || alarm.AlarmName}>
                    <span>{alarm.AlarmName}</span>
                    <span
                      className={`status-dot ${
                        String(alarm.AlarmState || "").toLowerCase() === "ok" ? "on" : "off"
                      }`}
                    ></span>
                  </div>
                ))
              ) : (
                <div className="status-row">
                  <span>No alarms available</span>
                  <span className="status-dot off"></span>
                </div>
              )}
              <div className="reboot-section-title">EC2 Reboots</div>
              {runningTargetsLoading || runningTargetsFetching ? (
                <div className="reboot-status">Loading reboot targets...</div>
              ) : runningTargetsError ? (
                <div className="reboot-status error">
                  Failed to load reboot targets{rebootTargetsErrorMessage ? `: ${rebootTargetsErrorMessage}` : "."}
                </div>
              ) : Array.isArray(runningTargets) && runningTargets.length > 0 ? (
                runningTargets.map((target) => {
                  const status = rebootStatusById[target.InstanceId];
                  return (
                    <div key={target.InstanceId} className="reboot-target-row">
                      <button
                        className="reboot-btn"
                        onClick={() => openRebootConfirm(target)}
                      >
                        Reboot {target.Name || target.InstanceId}
                      </button>
                      {status && (
                        <div
                          className={`reboot-status ${status.ok ? "ok" : "error"}`}
                        >
                          {status.message}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="reboot-status error">
                  No matching running instances found.
                </div>
              )}
            </Card>
          )}

          {/* WILSON CREEK */}
          {WilsonCreek && (
            <Card title={assetname || "Powerplant Status"}>
              <div className="status-center">
                <div
                  className={`status-dot large ${
                    powerplantStatus === true ? "on" : "off"
                  }`}
                ></div>
              </div>
              <div style={{ textAlign: "center", marginTop: "6px", fontSize: "13px" }}>
                {totalOutputData?.total_output != null
                  ? `${(Number(totalOutputData.total_output) / 1000).toFixed(2)} MW`
                  : "0.00 MW"}
              </div>
            </Card>
          )}

          {/* SUMMARY */}
          {Summary && (
            <Card title="Summary">
              <div>Revenue ({summaryCurrentMonthLabel}): ${Math.floor(currentMonthRevenue)}</div>
              <div>Revenue ({summaryPreviousMonthLabel}): ${Math.floor(previousMonthRevenue)}</div>
            </Card>
          )}

          {/* POWERPLANT CONFIGURATION */}
          <Card title="Powerplant Information">
            {AssetName && <div>Asset Name: {assetname}</div>}
            {AssetIdentifier && <div>Asset ID: {assetidentifier}</div>}
            {AssetType && <div>Asset Type: {assettype}</div>}
            {ActualCapacity && <div>Actual Capacity: {actualcapacity} MW</div>}
            {MinimumStableGeneration && (
              <div>Min Stable Gen: {minimumstablegeneration} MW</div>
            )}
            {MarginalCost && <div>Marginal Cost: ${marginalcost}</div>}
            {OfferControlParty && (
              <div>Offer Control Party: {offercontrolparty}</div>
            )}
            {PowerAvailability && (
              <div>Power Availability: {poweravailability} MW</div>
            )}
            {MinProfit && <div>Min Profit: ${minProfit}</div>}
            {UnavailableHours && (
              <div>Unavailable Hours: {unavailableHours.length}</div>
            )}
            {OptimizedRunLogic && (
              <div>
                Optimized Run Logic:{" "}
                {optimizedRunLogic ? "Enabled" : "Disabled"}
              </div>
            )}
            {McRunLogic && (
              <div>MC Run Logic: {mcRunLogic ? "Enabled" : "Disabled"}</div>
            )}
            {DefaultLogic && (
              <div>Default Logic: {defaultLogic ? "Enabled" : "Disabled"}</div>
            )}
            {MinRunHours && <div>Min Run Hours: {minrunhours}</div>}
            {MaxUnprofHours && <div>Max Unprof Hours: {maxunprofhours}</div>}
          </Card>
        </Box>
      )}
    </div>
      <Dialog
        open={rebootConfirmOpen}
        onClose={() => {
          setRebootConfirmOpen(false);
          setSelectedRebootTarget(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#0b1422",
            color: "#e7edf7",
            border: "1px solid #22416b",
            borderRadius: "12px",
            boxShadow: "0 16px 36px rgba(10, 22, 42, 0.6)",
          },
        }}
      >
        <DialogTitle>Confirm Reboot</DialogTitle>
        <DialogContent dividers>
          Are you sure you want to reboot{" "}
          <strong>{selectedRebootTarget?.Name || selectedRebootTarget?.InstanceId || "this instance"}</strong>?
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setRebootConfirmOpen(false);
              setSelectedRebootTarget(null);
            }}
            sx={{ color: "#e7edf7", borderColor: "#22416b" }}
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleReboot}>
            Reboot
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
